import { EmailDraftApi } from '@myre/app-agent-generated/sdk/angular/EmailDraftApi';
import { type EmailDraftContent } from '@myre/app-agent-generated/types/EmailDraftContent';
import { type Translated, verbatim } from '@myre/common-i18n';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import {
  ButtonComponent,
  ButtonTheme,
  TagComponent,
} from '@myre/ui-primitive-components';
import { TextboxComponent, WysiwygComponent } from '@myre/ui-primitive-forms';
import { ModalService } from '@myre/ui-primitive-modal';

import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  type OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import MarkdownIt from 'markdown-it';
import { firstValueFrom } from 'rxjs';
import TurndownService from 'turndown';

import { WORDINGS } from '../../../myre-wordings-loader';

import {
  type ApprovalEmailMessage,
  type EditedEmailContent,
  type EmailAttachmentRef,
} from '../../interfaces/chat.interfaces';

const _md = new MarkdownIt({ html: false, breaks: true });
const _turndown = new TurndownService({ headingStyle: 'atx' });

/** A recipient bucket the user can trim/extend in the modal. */
type RecipientField = 'to' | 'cc';

@Component({
  selector: 'app-email-draft-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    TagComponent,
    TextboxComponent,
    WysiwygComponent,
  ],
  templateUrl: './email-draft-modal.component.html',
  styleUrl: './email-draft-modal.component.scss',
})
export class EmailDraftModalComponent implements OnInit {
  public readonly uid = input.required<string>();
  public readonly message = input.required<ApprovalEmailMessage>();
  public readonly customerId = input.required<number>();

  /**
   * The addresses the agent surfaced from the data this conversation — the only
   * set a user may add a recipient from, so an edited list never escapes the
   * DB-read addresses (the server re-checks this regardless).
   */
  public readonly availableRecipients = input<readonly string[]>([]);

  /** Documents surfaced this conversation the user may attach (owner-verified server-side). */
  public readonly availableAttachments = input<readonly EmailAttachmentRef[]>(
    [],
  );

  public readonly draftConfirmed = output<EditedEmailContent>();

  protected readonly ButtonTheme = ButtonTheme;

  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected get _w() {
    return this._wordings._library.chat;
  }

  protected readonly _subject = signal<string>('');
  protected readonly _editorHtml = signal<string>('');
  protected readonly _instruction = signal<string>('');
  protected readonly _selectedText = signal<string>('');
  protected readonly _loading = signal<boolean>(false);

  protected readonly _to = signal<readonly string[]>([]);
  protected readonly _cc = signal<readonly string[]>([]);

  /** The connected user's blind copy — shown read-only, never editable. */
  protected readonly _bcc = signal<string>('');

  /**
   * Surfaced addresses not already selected in either bucket — what the "add"
   * pickers offer, so an address is never picked into both `to` and `cc`.
   */
  protected readonly _addable = computed<readonly string[]>(() => {
    const selected = new Set([...this._to(), ...this._cc()]);

    return this.availableRecipients().filter(email => !selected.has(email));
  });

  /** >1 recipient ⇒ they will be blind-copied and won't see each other. */
  protected readonly _recipientsPrivate = computed<boolean>(
    () => this._to().length + this._cc().length > 1,
  );

  /** Currently attached documents ({key,name}), editable in the modal. */
  protected readonly _attachments = signal<readonly EmailAttachmentRef[]>([]);

  /** Surfaced docs not already attached — what the "add" picker offers. */
  protected readonly _addableAttachments = computed<
    readonly EmailAttachmentRef[]
  >(() => {
    const selected = new Set(this._attachments().map(doc => doc.key));

    return this.availableAttachments().filter(doc => !selected.has(doc.key));
  });

  private readonly _modalService = inject(ModalService);
  private readonly _emailDraftApi = inject(EmailDraftApi);

  public ngOnInit(): void {
    const message = this.message();
    this._subject.set(message.subject);
    this._editorHtml.set(_md.render(message.body));
    this._to.set([...message.to]);
    this._cc.set([...(message.cc ?? [])]);
    this._bcc.set(message.bcc);

    // The current attachments arrive as parallel key + name arrays; zip them.
    const keys = message.attachments ?? [];
    const names = message.attachmentNames ?? [];
    this._attachments.set(
      keys.map((key, index) => ({ key, name: names[index] ?? key })),
    );
  }

  /**
   * The editor stays silent on blur (so the selection survives the user moving to
   * the AI-instruction field), emits the text on a real selection, and emits ''
   * when a click deselects while focused. So setting the value verbatim is right:
   * a real selection sets it, a deselect-click clears it, a blur keeps it.
   */
  protected _onSelectedText(text: string): void {
    this._selectedText.set(text);
  }

  /**
   * Editing the body invalidates any tracked selection (the snippet may no longer
   * exist — e.g. the user selected then deleted it), so clear it alongside the
   * content update.
   */
  protected _onEditorChange(html: string): void {
    this._editorHtml.set(html);
    this._selectedText.set('');
  }

  protected _clearSelection(): void {
    this._selectedText.set('');
  }

  /** A recipient address or an attachment file name, shown as is. */
  protected _chipText(value: string): Translated {
    return verbatim(value);
  }

  protected _removeRecipient(field: RecipientField, email: string): void {
    const target = field === 'to' ? this._to : this._cc;
    target.update(list => list.filter(item => item !== email));
  }

  protected _addRecipient(field: RecipientField, email: string): void {
    if (email.length === 0) {
      return;
    }

    const target = field === 'to' ? this._to : this._cc;
    target.update(list => (list.includes(email) ? list : [...list, email]));
  }

  protected _removeAttachment(key: string): void {
    this._attachments.update(list => list.filter(doc => doc.key !== key));
  }

  protected _addAttachment(key: string): void {
    if (key.length === 0) {
      return;
    }

    const doc = this.availableAttachments().find(item => item.key === key);

    if (doc === undefined || this._attachments().some(a => a.key === key)) {
      return;
    }

    this._attachments.update(list => [...list, doc]);
  }

  protected async _onRefine(): Promise<void> {
    const instruction = this._instruction().trim();

    if (!instruction || this._loading()) {
      return;
    }

    const currentContent: EmailDraftContent = {
      subject: this._subject(),
      body: _turndown.turndown(this._editorHtml()),
    };
    const selectedText = this._selectedText() || undefined;

    this._loading.set(true);

    try {
      const result = await firstValueFrom(
        this._emailDraftApi.refine(
          { customerId: this.customerId() },
          { instruction, selectedText, currentContent },
        ),
      );

      this._subject.set(result.subject);
      this._editorHtml.set(_md.render(result.body));
      this._instruction.set('');
      this._clearSelection();
    } finally {
      this._loading.set(false);
    }
  }

  protected _onConfirm(): void {
    this.draftConfirmed.emit({
      subject: this._subject(),
      body: _turndown.turndown(this._editorHtml()),
      to: this._to(),
      cc: this._cc(),
      attachments: this._attachments().map(doc => doc.key),
    });
  }

  protected _onClose(): void {
    this._modalService.close(this.uid());
  }
}
